import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { articles as ArticlesType } from '@/mocks/articles';

interface ArticleCardProps {
  article: (typeof ArticlesType)[number];
  variant?: 'default' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-primary-50/50 transition-colors"
      >
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">
            {article.publishDate} · {article.readTime}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {article.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
          {article.summary}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}